import { SEPARATION_IN_BETWEEN, calculateGeneralMedianVcg, generateCoordFunctions, sortRowsArray, } from "@tedivo/tedivo-bay-grid-pure";
import { BayLevelEnum, } from "open-vessel-definition";
import { generateSideContainerSymbols, } from "./generateSideContainerSymbols";
import { SetEx, createDictionaryMultiple, feetToMillimeters, roundDec, } from "@tedivo/tedivo-pure-helpers";
import { createSvgPath } from "../helpers/createSvgPath";
import { drawSideBridge } from "./drawParts/drawSideBridge";
import { drawSideCrane } from "./drawParts/drawSideCrane";
import { drawSideSmokeStack } from "./drawParts/drawSideSmokeStack";
import { setTopBayNumbers } from "../helpers/setTopBayNumbers";
export const SVG_WIDTH = 1500;
export const DISTANCE_TO_CONTAINER_FOR_SHIP_LINE = 4000;
export function createSimpleSideView({ prefixSymbol = "smbCn", sizeSummary, baysData, lcgVcgTcgAndPairings, symbolsOptions, vesselPartsData = [], adjustedBottomBases = [], containersWithPositions, selectContainerCriteria, bayNumbersDoubleClickAction, }) {
    const { bayLevelPositionsAbove, bayLevelPositionsBelow, missingImportantVcgs, missingImportantLcgs, missingImportantTcgs, allIsoBays: isoBaysArray, cgStats: { totalSlotsCount }, lcgsBy20Bay, pairedBaysCalc: pairedBays, blockBaysAndSizes, minTierAbove, } = lcgVcgTcgAndPairings;
    const missingImportantXcgs = missingImportantTcgs || missingImportantLcgs || missingImportantVcgs;
    // 1. Add missing LCGs and VCGs if needed, return x,y functions
    const coordFns = generateCoordFunctions({
        sizeSummary,
        baysData,
        vesselPartsData,
        bayLcvVcgTcgAndPairings: lcgVcgTcgAndPairings,
        maxHeightAbsolute: containersWithPositions.maxHeightAbsolute,
    });
    if (!coordFns) {
        throw new Error("Could not generate coord functions");
    }
    const { xRange, yRange, xPos, yPos, scaled, addX, svgSizeRatio, lastBaySizeInMM, bowLength, sternLength, allPartsLengthInMeters, vOneBaySlots, } = coordFns;
    const { generalMedianVcg: midVcg, midVcgByBay, adjustedBottomBasesDict, } = calculateGeneralMedianVcg(containersWithPositions.bayLevelCgsInfo, missingImportantVcgs, adjustedBottomBases);
    // 2. SVG Node
    const svgNode = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    if (xRange)
        svgNode.setAttribute("width", String(xRange));
    if (yRange)
        svgNode.setAttribute("height", String(yRange));
    if (xRange && yRange)
        svgNode.setAttribute("viewBox", `0 0 ${xRange} ${yRange}`);
    svgNode.setAttribute("data-scale", String(roundDec(svgSizeRatio, 5)));
    // 3. Add symbols
    const symbols = generateSideContainerSymbols(symbolsOptions || {}, svgSizeRatio, prefixSymbol);
    const symbolsNames = Object.keys(symbols);
    symbolsNames.forEach((key) => {
        svgNode.appendChild(symbols[key]);
    });
    // 4. Top bay numbers
    const drawTopBayNumbers = (blps) => setTopBayNumbers({
        blps,
        lcgsBy20Bay,
        svgNode,
        xPos,
        fontColor: symbolsOptions && symbolsOptions.fontColor,
        onDoubleClick: bayNumbersDoubleClickAction,
    });
    if (bayLevelPositionsAbove.length)
        drawTopBayNumbers(bayLevelPositionsAbove);
    else
        drawTopBayNumbers(bayLevelPositionsBelow);
    let svgBayLevelGroups = [];
    if (totalSlotsCount > 0) {
        // 5. Ship line
        drawShipLine();
        drawVesselParts(midVcg);
        // 6. Draw containers
        svgBayLevelGroups = drawSideContainers(bayLevelPositionsAbove, bayLevelPositionsBelow, pairedBays, adjustedBottomBasesDict, containersWithPositions, minTierAbove);
        // 7. Draw bulkheads
        drawBulkheads(xRange, midVcgByBay);
        //_helperDraw();
    }
    return {
        sideViewSvg: svgNode,
        missingImportantXcgs,
        xRange,
        yRange,
        svgSizeRatio,
        xPos,
        yPos,
        svgBayLevelGroups,
    };
    function drawSideContainers(bayLevelPositionsAbove, bayLevelPositionsBelow, calculatedPairedBays, adjustedBottomBasesDict, containersWithPositions, minTierAbove) {
        const svgGroups = [];
        const containersByBays = createDictionaryMultiple(selectContainersToDraw(containersWithPositions, selectContainerCriteria), (c) => c.isoOddBay);
        [
            ...calculatedPairedBays.pairedBays,
            ...calculatedPairedBays.unpairedBays,
        ].forEach((pb) => {
            const blps = (pb.level === BayLevelEnum.ABOVE
                ? bayLevelPositionsAbove
                : bayLevelPositionsBelow).filter((blp) => pb.allBays.indexOf(blp.isoBay) >= 0);
            const isoBays = blps
                .map((b) => b.isoBay)
                .filter((b, i, a) => a.indexOf(b) === i);
            const level = blps[0].level;
            const svgGroupResp = createSvgGroup(isoBays, level, adjustedBottomBasesDict, containersWithPositions);
            if (!svgGroupResp)
                return;
            svgGroups.push(svgGroupResp);
            drawContainers({
                isoBays,
                level,
                minTierAbove,
                svgGroup: svgGroupResp.svgGroup,
                containersByBays,
                minX: svgGroupResp.xMin,
                minY: missingImportantXcgs
                    ? svgGroupResp.yMin
                    : svgGroupResp.minYforGroup || 0,
            });
            svgNode.appendChild(svgGroupResp.svgGroup);
        });
        return svgGroups;
        function createSvgGroup(isoBays, level, adjustedBottomBasesDict, containersWithPositions) {
            var _a, _b, _c;
            const isoBaseBay = isoBays[0];
            const isoPairedBay = isoBays[1];
            const levelBaseBay = level;
            const adjustBB = (_b = (_a = adjustedBottomBasesDict[`${isoBaseBay}-${levelBaseBay}`]) !== null && _a !== void 0 ? _a : adjustedBottomBasesDict[`${isoPairedBay}-${levelBaseBay}`]) !== null && _b !== void 0 ? _b : 0;
            const { bayLevelCgsInfo: bayLevelVcgsInfo } = containersWithPositions;
            const bayLevelInfo = (_c = bayLevelVcgsInfo[isoBaseBay]) === null || _c === void 0 ? void 0 : _c[levelBaseBay];
            const height = bayLevelInfo === null || bayLevelInfo === void 0 ? void 0 : bayLevelInfo.maxHeight;
            const bottomBase = bayLevelInfo === null || bayLevelInfo === void 0 ? void 0 : bayLevelInfo.bottomBase;
            if (height === undefined || bottomBase === undefined)
                return undefined;
            const lcgs20OfBay = lcgsBy20Bay[isoBaseBay];
            const xMin = xPos(lcgs20OfBay.minAftLcg);
            const xMax = xPos(lcgs20OfBay.maxForeLcg);
            const yMin = roundDec(yPos(bottomBase + adjustBB), 3);
            const yMax = roundDec(yPos(bottomBase + adjustBB + height), 3);
            const svgGroup = document.createElementNS("http://www.w3.org/2000/svg", "g");
            svgGroup.setAttribute("data-id", `${isoBays.join("-")}|${level}`);
            svgGroup.setAttribute("data-bbase", `${bayLevelInfo === null || bayLevelInfo === void 0 ? void 0 : bayLevelInfo.bottomTier}`);
            svgGroup.setAttribute("data-bay", isoBaseBay);
            svgGroup.setAttribute("data-height", `${height}`);
            if (missingImportantXcgs)
                svgGroup.setAttribute("data-action", `move`);
            svgGroup.setAttribute("transform", `translate(${xMin}, ${yMin})`);
            // const rect = document.createElementNS(
            //   "http://www.w3.org/2000/svg",
            //   "rect",
            // );
            // rect.setAttribute("x", "0");
            // rect.setAttribute("y", "0");
            // rect.setAttribute("width", `${maxX - minX}`);
            // rect.setAttribute("height", `${maxY - minYforGroup}`);
            // rect.setAttribute("fill", "none");
            // rect.setAttribute("stroke", "red");
            // rect.setAttribute("stroke-width", "1");
            // rect.setAttribute("stroke-dasharray", "5,5");
            return {
                svgGroup,
                xMin,
                yMin,
                xMax,
                yMax,
                minYforGroup: yMin,
            };
        }
        /** Draws the containers of a single bay-and-level */
        function drawContainers({ isoBays, level, minTierAbove, svgGroup, minX, minY, containersByBays, }) {
            const fltrLevelFn = level == BayLevelEnum.ABOVE
                ? (c) => c.bottomTier !== undefined && Number(c.bottomTier) >= minTierAbove
                : (c) => c.bottomTier !== undefined && Number(c.bottomTier) < minTierAbove;
            isoBays
                .flatMap((b) => containersByBays[b] || [])
                .filter(fltrLevelFn)
                .forEach((c) => {
                if (c.lcg === undefined ||
                    c.tierBottomBase === undefined ||
                    c.meta === undefined)
                    return;
                const cellPos = `${c.meta.row}${c.meta.tier}`;
                const contSvg = document.createElementNS("http://www.w3.org/2000/svg", "use");
                contSvg.setAttribute("href", `#smbCn${c.size}-${c.extending || 0}`);
                contSvg.setAttribute("x", `${roundDec(xPos(c.lcg) - minX, 3)}`);
                contSvg.setAttribute("y", `${roundDec(yPos(c.tierBottomBase) - minY, 3)}`);
                contSvg.setAttribute("data-p", `${cellPos}`);
                contSvg.setAttribute("data-bay", c.isoOddBay);
                const contTitle = document.createElementNS("http://www.w3.org/2000/svg", "title");
                contTitle.innerHTML = `${c.isoOddBay}-${cellPos}`;
                contSvg.appendChild(contTitle);
                svgGroup.appendChild(contSvg);
            });
            svgNode.appendChild(svgGroup);
        }
        function selectContainersToDraw(containersWithPositions, selectContainerCriteria) {
            const { containerCgsByBayAndLevelAndRow, containersCgsArray } = containersWithPositions;
            const byBayAndLevelAndRowKeys = Object.keys(containerCgsByBayAndLevelAndRow).sort();
            const subsetOfContainersToDraw = [];
            switch (selectContainerCriteria) {
                case "maxSize":
                    {
                        loopOverContainers((allRowsSetExt, allTiersSetExt, pb, containerMatrixPerBayAndLevel) => {
                            var _a;
                            const allRows = Array.from(allRowsSetExt).sort(sortRowsArray); // Port to stbd
                            const allTiers = Array.from(allTiersSetExt)
                                .filter(Boolean)
                                .sort(); // Bottom to top
                            for (const tier of allTiers) {
                                const has20 = new Set();
                                let has40 = false;
                                const subsetOfContainersToDrawInTier = [];
                                loopRows: for (const row of allRows) {
                                    // Now we loop over the bays
                                    for (const bay of pb.allBays) {
                                        const infoByBayAndLevel = containerMatrixPerBayAndLevel[`${bay}-${pb.level}`];
                                        if (!infoByBayAndLevel)
                                            continue;
                                        const cont = (_a = infoByBayAndLevel[tier]) === null || _a === void 0 ? void 0 : _a[row];
                                        if (!cont)
                                            continue;
                                        if (cont.size >= 40)
                                            has40 = true;
                                        if (!has20.has(bay) || cont.size >= 40) {
                                            subsetOfContainersToDrawInTier.push(cont);
                                            if (cont.size < 40)
                                                has20.add(bay);
                                            if (cont.size >= 40)
                                                break loopRows;
                                        }
                                    }
                                }
                                subsetOfContainersToDraw.push(...subsetOfContainersToDrawInTier.filter((c) => !has40 || c.size >= 40));
                            }
                        });
                    }
                    break;
                case "all":
                    subsetOfContainersToDraw.push(...containersCgsArray.slice());
                    break;
                case "stbdOuter":
                    {
                        loopOverContainers((allRowsSetExt, allTiersSetExt, pb, containerMatrixPerBayAndLevel) => {
                            var _a;
                            const allRows = Array.from(allRowsSetExt)
                                .sort(sortRowsArray)
                                .reverse(); // Stbd to port
                            const allTiers = Array.from(allTiersSetExt)
                                .filter(Boolean)
                                .sort(); // Bottom to top
                            loopTiers: for (const tier of allTiers) {
                                const has20 = new Set();
                                loopRows: for (const row of allRows) {
                                    for (const bay of pb.allBays) {
                                        const infoByBayAndLevel = containerMatrixPerBayAndLevel[`${bay}-${pb.level}`];
                                        if (!infoByBayAndLevel)
                                            continue;
                                        const cont = (_a = infoByBayAndLevel[tier]) === null || _a === void 0 ? void 0 : _a[row];
                                        if (!cont)
                                            continue;
                                        if (!has20.has(bay) || cont.size >= 40) {
                                            subsetOfContainersToDraw.push(cont);
                                            if (cont.size < 40)
                                                has20.add(bay);
                                            if (has20.size === pb.allBays.length)
                                                continue loopTiers;
                                            if (cont.size >= 40)
                                                break loopRows;
                                        }
                                    }
                                }
                            }
                        });
                    }
                    break;
                case "portOuter":
                    {
                        loopOverContainers((allRowsSetExt, allTiersSetExt, pb, containerMatrixPerBayAndLevel) => {
                            var _a;
                            const allRows = Array.from(allRowsSetExt).sort(sortRowsArray); // Port to stbd
                            const allTiers = Array.from(allTiersSetExt)
                                .filter(Boolean)
                                .sort(); // Bottom to top
                            loopTiers: for (const tier of allTiers) {
                                const has20 = new Set();
                                loopRows: for (const row of allRows) {
                                    for (const bay of pb.allBays) {
                                        const infoByBayAndLevel = containerMatrixPerBayAndLevel[`${bay}-${pb.level}`];
                                        if (!infoByBayAndLevel)
                                            continue;
                                        const cont = (_a = infoByBayAndLevel[tier]) === null || _a === void 0 ? void 0 : _a[row];
                                        if (!cont)
                                            continue;
                                        if (!has20.has(bay) || cont.size >= 40) {
                                            subsetOfContainersToDraw.push(cont);
                                            if (cont.size < 40)
                                                has20.add(bay);
                                            if (has20.size === pb.allBays.length)
                                                continue loopTiers;
                                            if (cont.size >= 40)
                                                break loopRows;
                                        }
                                    }
                                }
                            }
                        });
                    }
                    break;
                default: {
                    const rowSelected = selectContainerCriteria;
                    const unique = new Map();
                    byBayAndLevelAndRowKeys.forEach((key) => {
                        const [, , row] = key.split("-");
                        if (row === rowSelected) {
                            const conts = containerCgsByBayAndLevelAndRow[key];
                            if (conts)
                                conts.forEach((c) => {
                                    var _a, _b;
                                    if ((_a = c.meta) === null || _a === void 0 ? void 0 : _a.position)
                                        unique.set((_b = c.meta) === null || _b === void 0 ? void 0 : _b.position, c);
                                });
                        }
                    });
                    subsetOfContainersToDraw.push(...Array.from(unique.values()));
                    break;
                }
            }
            return subsetOfContainersToDraw;
            function loopOverContainers(filterFn) {
                const subsetOfContainersToDraw = [];
                const { containerMatrixPerBayAndLevel, allTiersInBayAndLevel, allRowsInBayAndLevel, } = createContainersMatrixPerBayAndLevel();
                [
                    ...calculatedPairedBays.pairedBays,
                    ...calculatedPairedBays.unpairedBays,
                ].forEach((pb) => {
                    const allRowsSetExt = new SetEx();
                    const allTiersSetExt = new SetEx();
                    pb.allBays.forEach((bay) => {
                        const key = `${bay}-${pb.level}`;
                        const allRowsSet = allRowsInBayAndLevel.get(key);
                        const allTiersSet = allTiersInBayAndLevel.get(key);
                        if (!allRowsSet || !allTiersSet)
                            return;
                        allRowsSetExt.merge(allRowsSet);
                        allTiersSetExt.merge(allTiersSet);
                    });
                    filterFn(allRowsSetExt, allTiersSetExt, pb, containerMatrixPerBayAndLevel);
                });
                return subsetOfContainersToDraw;
            }
            function createContainersMatrixPerBayAndLevel() {
                const containerMatrixPerBayAndLevel = {};
                const allTiersInBayAndLevel = new Map();
                const allRowsInBayAndLevel = new Map();
                byBayAndLevelAndRowKeys.forEach((key) => {
                    const [bay, levelStr, row] = key.split("-");
                    if (!containerMatrixPerBayAndLevel[`${bay}-${levelStr}`])
                        containerMatrixPerBayAndLevel[`${bay}-${levelStr}`] = {};
                    const contsOfRow = containerCgsByBayAndLevelAndRow[key];
                    if (!contsOfRow)
                        return;
                    contsOfRow.forEach((c) => {
                        var _a, _b, _c;
                        const tier = (_a = c.meta) === null || _a === void 0 ? void 0 : _a.tier;
                        if (!tier)
                            return;
                        if (!containerMatrixPerBayAndLevel[`${bay}-${levelStr}`][tier])
                            containerMatrixPerBayAndLevel[`${bay}-${levelStr}`][tier] = {};
                        containerMatrixPerBayAndLevel[`${bay}-${levelStr}`][tier][row] = c;
                        if (!allTiersInBayAndLevel.has(`${bay}-${levelStr}`))
                            allTiersInBayAndLevel.set(`${bay}-${levelStr}`, new Set());
                        if (!allRowsInBayAndLevel.has(`${bay}-${levelStr}`))
                            allRowsInBayAndLevel.set(`${bay}-${levelStr}`, new Set());
                        (_b = allTiersInBayAndLevel
                            .get(`${bay}-${levelStr}`)) === null || _b === void 0 ? void 0 : _b.add(tier);
                        (_c = allRowsInBayAndLevel
                            .get(`${bay}-${levelStr}`)) === null || _c === void 0 ? void 0 : _c.add(row);
                    });
                });
                return {
                    containerMatrixPerBayAndLevel,
                    allTiersInBayAndLevel,
                    allRowsInBayAndLevel,
                };
            }
        }
    }
    function drawShipLine() {
        var _a;
        let xy = undefined;
        let lastWasWrong = false;
        const segmentsXy = [
            {
                x: roundDec(addX * 0.5 + lastBaySizeInMM),
                y: yRange - 1,
                bay: "bottom",
            }, // bottom-left
            {
                x: roundDec(xRange - bowLength * svgSizeRatio * 0.5),
                y: yRange - 1,
                bay: "bottom",
            }, // bottom-right
            {
                x: roundDec(xRange),
                y: roundDec(yRange * 0.875),
                bX: roundDec(xRange - bowLength * svgSizeRatio * 0.25),
                bY: yRange - 1,
                bay: "bottom",
            },
            {
                x: roundDec(xRange - bowLength * svgSizeRatio * 0.5),
                y: roundDec(yRange * 0.75),
                bX: roundDec(xRange - bowLength * svgSizeRatio * 0.15),
                bY: roundDec(yRange * 0.75),
                bay: "bottom",
            },
            {
                x: roundDec(xRange - bowLength * svgSizeRatio * 0.5),
                y: roundDec(yRange * 0.75),
                bay: "bottom",
            },
        ];
        // #region add-nose
        const firstBay = (_a = isoBaysArray.find((b) => midVcgByBay[b.isoBay])) === null || _a === void 0 ? void 0 : _a.isoBay;
        if (firstBay) {
            const firstVcg = midVcgByBay[firstBay];
            // top-right (nose of the ship)
            const bowTop = yPos(firstVcg + DISTANCE_TO_CONTAINER_FOR_SHIP_LINE, 1);
            segmentsXy.push({
                x: xRange,
                y: bowTop,
                bay: "nose1",
            }, {
                x: roundDec(xPos(allPartsLengthInMeters, 1) +
                    scaled(DISTANCE_TO_CONTAINER_FOR_SHIP_LINE)),
                y: bowTop,
                bay: "nose2",
            }, {
                x: xPos(allPartsLengthInMeters, 1),
                y: yPos(firstVcg, 1),
                bay: "nose3",
            });
        }
        // #endregion add-nose
        let prevX = Infinity;
        let prevY = undefined;
        const THREE_METERS_IN_X = feetToMillimeters(10) * svgSizeRatio;
        blockBaysAndSizes.forEach((block) => {
            var _a;
            const [isoBayBase, isoBayPaired] = block.allBays.split("-");
            if (block.numberOfBays === 1 && block.blockSize >= 40) {
                lastWasWrong = true;
            }
            const midVcg = (_a = midVcgByBay[isoBayBase]) !== null && _a !== void 0 ? _a : midVcgByBay[isoBayPaired];
            if (midVcg === undefined) {
                lastWasWrong = true;
                return;
            }
            const newForeX = xPos(lcgsBy20Bay[isoBayBase].maxForeLcg);
            const newAftX = xPos(lcgsBy20Bay[isoBayBase].minAftLcg);
            const y = yPos(midVcg, 1);
            if (prevY !== undefined &&
                prevY !== y &&
                Math.abs(newForeX - prevX) < THREE_METERS_IN_X) {
                const midX = (newForeX + prevX) * 0.5;
                segmentsXy.push({ x: midX, y: prevY, bay: isoBayBase });
                segmentsXy.push({ x: midX, y: y, move: lastWasWrong, bay: isoBayBase });
            }
            xy = { x: newForeX, y, bay: isoBayBase };
            if (lastWasWrong)
                xy.move = true;
            segmentsXy.push(xy);
            xy = { x: newAftX, y, bay: isoBayBase };
            if (lastWasWrong)
                xy.move = true;
            segmentsXy.push(xy);
            prevX = Math.min(xy.x, prevX);
            prevY = xy.y;
            lastWasWrong = false;
        });
        // Finally, add stern
        if (xy !== undefined) {
            segmentsXy.push({
                x: roundDec(addX * 0.6),
                y: xy.y,
                move: lastWasWrong,
                bay: "stern",
            }, {
                x: roundDec(addX * 0.25),
                y: xy.y,
                bay: "stern",
            });
        }
        segmentsXy.push({ x: roundDec(addX * 0.3, 2), y: roundDec(yRange * 0.7), bay: "stern" }, { x: roundDec(addX * 0.4), y: roundDec(yRange * 0.7), bay: "stern" });
        // Prepare SVG paths
        const svgOk = createSvgPath({
            strokeColor: symbolsOptions === null || symbolsOptions === void 0 ? void 0 : symbolsOptions.shipStrokeColor,
            strokeWidth: symbolsOptions === null || symbolsOptions === void 0 ? void 0 : symbolsOptions.strokeWidth,
            fillOpactiy: 0,
        });
        svgOk.dataset["edx"] = "shipLine";
        const svgWrong = createSvgPath({
            strokeColor: (symbolsOptions === null || symbolsOptions === void 0 ? void 0 : symbolsOptions.warningColor) || "red",
            strokeWidth: symbolsOptions === null || symbolsOptions === void 0 ? void 0 : symbolsOptions.strokeWidth,
            fillOpactiy: 0,
        });
        svgWrong.dataset["edx"] = "shipLineErrors";
        const pathArr = [];
        const pathArrWrong = [];
        let prevXyOk;
        segmentsXy.forEach((xy, i) => {
            if (xy.bX === undefined) {
                pathArr.push(`${i === 0 || xy.move ? "M" : "L"}${xy.x},${xy.y}`);
            }
            else {
                pathArr.push(`Q${xy.bX},${xy.bY} ${xy.x},${xy.y}`);
            }
            if (xy.move && prevXyOk) {
                pathArrWrong.push("M" + prevXyOk.x + "," + prevXyOk.y);
                pathArrWrong.push("L" + xy.x + "," + xy.y);
            }
            prevXyOk = xy;
        });
        const pathStr = `${pathArr.join(" ")} L${segmentsXy[0].x},${segmentsXy[0].y}`;
        if (pathStr.indexOf("Infinity") < 0) {
            svgOk.setAttribute("d", pathStr);
            svgWrong.setAttribute("d", pathArrWrong.join(" "));
        }
        svgNode.appendChild(svgOk);
        if (pathArrWrong.length)
            svgNode.appendChild(svgWrong);
        return yPos(midVcg);
    }
    function drawBulkheads(xRange, midVcgByBay) {
        bayLevelPositionsBelow
            .filter((m) => m.bulkhead && (m.bulkhead.fore || m.bulkhead.aft))
            .forEach((maxByTier) => {
            const isoBaseBay = maxByTier.isoBay;
            const midVcg = midVcgByBay[isoBaseBay];
            if (maxByTier.bulkhead) {
                const hasFore = maxByTier.bulkhead.fore;
                const hasAft = maxByTier.bulkhead.aft;
                const foreLcg = lcgsBy20Bay[maxByTier.isoBay].maxForeLcg + SEPARATION_IN_BETWEEN;
                const aftLcg = lcgsBy20Bay[maxByTier.isoBay].minAftLcg - SEPARATION_IN_BETWEEN;
                if (hasFore) {
                    addBulkSvgNode(foreLcg, midVcg);
                }
                if (hasAft) {
                    addBulkSvgNode(aftLcg, midVcg);
                }
            }
        });
        function addBulkSvgNode(lcg, maxV) {
            const BULK_WIDTH = 1;
            if (!lcg || !maxV)
                return;
            const x = xPos(lcg);
            if (Math.abs(x / xRange) > 1.5)
                return;
            const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
            path.setAttribute("data-type", "bulkhead");
            path.setAttribute("stroke", (symbolsOptions === null || symbolsOptions === void 0 ? void 0 : symbolsOptions.shipStrokeColor) || "red");
            path.setAttribute("fill", (symbolsOptions === null || symbolsOptions === void 0 ? void 0 : symbolsOptions.shipStrokeColor) || "red");
            path.setAttribute("stroke-width", String((symbolsOptions === null || symbolsOptions === void 0 ? void 0 : symbolsOptions.strokeWidth) || 0.1));
            path.setAttribute("fill-opacity", "1");
            path.setAttribute("d", `M${xPos(lcg + BULK_WIDTH)},${yPos(maxV)} h${BULK_WIDTH} V${yRange - 1} h-${BULK_WIDTH} z`);
            svgNode.appendChild(path);
        }
    }
    function drawVesselParts(midVcg) {
        const rightOfShip = scaled(allPartsLengthInMeters + sternLength) + addX;
        vOneBaySlots.forEach((vOneBay, idx) => {
            var _a, _b;
            if (vOneBay.type === "BAY")
                return;
            const posX100 = vOneBay.positionX * 100;
            const nextPosX100 = ((_a = vOneBaySlots[idx + 1]) === null || _a === void 0 ? void 0 : _a.positionX) * 100;
            let xtra = 0;
            const nextLen = (_b = vOneBaySlots[idx + 1]) === null || _b === void 0 ? void 0 : _b.len;
            if (nextLen !== undefined && nextLen > 40) {
                xtra = scaled(feetToMillimeters(nextLen - 40) * 0.5);
            }
            const cPosX = feetToMillimeters(posX100);
            const nPosX = feetToMillimeters(nextPosX100 ? nextPosX100 - xtra : posX100 + vOneBay.len);
            const x = rightOfShip - scaled(nPosX * 0.5 + cPosX * 0.5);
            let paths = undefined;
            switch (vOneBay.type) {
                case "BRG":
                    paths = drawSideBridge(vOneBay, midVcg, svgSizeRatio);
                    break;
                case "SMK":
                    paths = drawSideSmokeStack(vOneBay, midVcg, svgSizeRatio);
                    break;
                case "CRN":
                    paths = drawSideCrane(vOneBay, midVcg, svgSizeRatio);
                    break;
            }
            if (paths) {
                paths.forEach((p) => {
                    p.setAttribute("transform", `translate(${x}, ${0})`);
                    svgNode.appendChild(p);
                });
            }
        });
    }
    // function _helperDraw() {
    //   const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
    //   path.setAttribute("stroke", "red");
    //   path.setAttribute("stroke-width", String(symbolsOptions?.strokeWidth || 1));
    //   path.setAttribute("fill-opacity", "0");
    //   path.setAttribute("d", `M${xPos(1955)},0 L${xPos(1955)},${yRange}`);
    //   svgNode.appendChild(path);
    // }
}
